export type InputType =
  | "text"
  | "textarea"
  | "number"
  | "decimal"
  | "checkbox"
  | "select"
  | "multiselect"
  | "date"
  | "rows"
  | "image"
  | "remoteSelect"
  | "textList";
export type FieldSchema = {
  key: string;
  label: string;
  input: InputType;
  section?: string;
  subsection?: string;
  subsectionCard?: boolean;
  placeholder?: string;
  options?: { master: string } | { enum: true };
  hidden?: boolean;
  hiddenReason?: string;
  readOnly?: boolean;
  uiOnly?: boolean;
  fields?: FieldSchema[];
  defaultValue?: any;
  ignoreDefault?: boolean;
  createPackageOnly?: boolean;
  feature?: "description";
  dependsOn?: string;
  showWhen?: string;
  multiple?: boolean;
  remote?: {
    kind?: string;
    source?: "ingredient" | "manufacturer";
    editableOnly?: boolean;
    excludeSelf?: boolean;
  };
};
export type KindSchema = { kind: string; label: string; enabled: boolean; fields: FieldSchema[] };
export type FieldMetadata = {
  required?: boolean;
  read_only?: boolean;
  max_length?: number;
  allow_null?: boolean;
  allow_blank?: boolean;
  min_value?: number;
  max_value?: number;
  decimal_places?: number;
  max_digits?: number;
  default?: any;
  type?: string;
  choices?: { value: string | number; display_name: string }[];
  children?: Record<string, FieldMetadata>;
  child?: FieldMetadata;
};
export type Metadata = Record<string, FieldMetadata>;
export type SelectOption = { key?: string | number; value: any; text: string; code?: string };

const field = (
  key: string,
  label: string,
  input: InputType = "text",
  section = "ข้อมูลเฉพาะประเภท"
): FieldSchema => ({ key, label, input, section });
const select = (key: string, label: string, master: string, multiple = false): FieldSchema => ({
  ...field(key, label, multiple ? "multiselect" : "select"),
  options: { master },
});
const enumeration = (key: string, label: string): FieldSchema => ({
  ...field(key, label, "select"),
  options: { enum: true },
});
const hidden = (key: string, label: string, reason: string): FieldSchema => ({
  ...field(key, label),
  hidden: true,
  hiddenReason: reason,
});
const readonly = (key: string, label: string): FieldSchema => ({
  ...field(key, label),
  readOnly: true,
});

export const commonFields: FieldSchema[] = [
  field("code", "รหัสบริการ", "text", "ข้อมูลทั่วไป"),
  field("name", "ชื่อบริการ (TH)", "text", "ข้อมูลทั่วไป"),
  field("name_en", "ชื่อบริการ (ENG)", "text", "ข้อมูลทั่วไป"),
  { ...select("p_type", "ประเภทบริการ", "catalogProductType"), section: "ข้อมูลทั่วไป" },
  { ...select("unit", "หน่วยขาย", "unitCore"), section: "ข้อมูลทั่วไป" },
  { ...enumeration("active_flag", "สถานะการเปิดขาย"), section: "ข้อมูลทั่วไป" },
  field("is_affiliate_exclusive", "ขายเฉพาะผ่าน Affiliate", "checkbox", "ข้อมูลทั่วไป"),
  field("is_vat_applicable", "คิดภาษีมูลค่าเพิ่ม (VAT 7%)", "checkbox", "ข้อมูลทั่วไป"),
  {
    ...field("create_package", "สร้าง Package จากรายการนี้", "checkbox", "ข้อมูลทั่วไป"),
    uiOnly: true,
    createPackageOnly: true,
  },
  { ...readonly("has_lots", "มีล็อตสินค้า"), section: "ข้อมูลทั่วไป" },
  { ...readonly("overall_cost", "ต้นทุนรวม"), section: "ข้อมูลทั่วไป" },
  {
    ...field("description", "รายละเอียดเพิ่มเติม", "textarea", "ข้อมูลทั่วไป"),
    feature: "description",
    hiddenReason: "รอ BA ยืนยันการเปิดใช้",
  },
];

export const pricingFields: FieldSchema[] = [
  select("bill_mode", "รูปแบบการเรียกเก็บเงิน", "billMode"),
  field("price_normal", "ราคาปกติ", "decimal"),
  field("price_special", "ราคาพิเศษ", "decimal"),
  field("price_premium", "ราคาพรีเมียม", "decimal"),
  field("price_foreign", "ราคาต่างชาติ", "decimal"),
  field("start_date", "วันที่เริ่มต้น", "date"),
  field("end_date", "วันที่สิ้นสุด", "date"),
];

export const editPricingFields: FieldSchema[] = [
  { ...pricingFields[0], label: "Bill mode" },
  { ...pricingFields[5], label: "วันเริ่มต้นใช้ราคานี้" },
  { ...pricingFields[6], label: "วันสิ้นสุดราคานี้" },
  ...pricingFields.slice(1, 5),
];

const drugFields: FieldSchema[] = [
  select("dosage_form", "รูปแบบยา", "dosageForm"),
  field("drug_name", "ชื่อทางการค้า"),
  enumeration("drug_name_format", "รูปแบบชื่อยา"),
  field("custom_generic", "ชื่อสามัญที่กำหนดเอง", "textarea"),
  {
    ...field("drug_ingredients", "ส่วนประกอบยา", "rows"),
    fields: [
      { ...field("ingredient", "ตัวยาสำคัญ", "remoteSelect"), remote: { source: "ingredient" } },
      field("strength", "ความแรง"),
      field("sequence", "ลำดับ", "number"),
    ],
  },
  readonly("generic_name", "ชื่อสามัญ"),
  readonly("synonym_name", "ชื่อสามัญอื่น"),
  readonly("ingredient_vtm", "รหัสส่วนประกอบยา"),
  readonly("drug_name_from_format", "ชื่อยาจากรูปแบบ"),
  readonly("drug_list_legacy", "บัญชียาเดิม"),
  enumeration("drug_list", "บัญชียาหลักแห่งชาติ"),
  enumeration("classification", "ประเภทของยา"),
  enumeration("pregcat", "ความเสี่ยงขณะตั้งครรภ์"),
  field("pregcat_description", "คำอธิบายความเสี่ยงขณะตั้งครรภ์", "textarea"),
  field("strength", "ความแรงของยา"),
  field("container", "จำนวนหน่วยใช้ต่อหน่วยขาย"),
  field("pronunciation_trade", "คำอ่านชื่อทางการค้า"),
  field("pronunciation", "คำอ่านชื่อสามัญ"),
  field("indication_th", "ข้อบ่งใช้", "textarea"),
  field("indication_en", "ข้อบ่งใช้ภาษาอังกฤษ", "textarea"),
  select("helper_labels", "ฉลากช่วย", "catalogHelperLabel", true),
  field("extra_note", "หมายเหตุเพิ่มเติมนอกฉลาก", "textarea"),
  field("factsheet", "ลิงก์รายละเอียดของยา"),
  ...[
    ["highalert", "ยาความเสี่ยงสูง"],
    ["vital", "ยาช่วยชีวิต"],
    ["lasa", "ชื่อยาคล้ายกัน"],
    ["stat", "สั่งเร่งด่วนได้"],
    ["individual_use", "ยาสำหรับผู้ป่วยเฉพาะราย"],
    ["to_be_tracked", "ติดตามการใช้"],
    ["research_drug", "ยาวิจัย"],
    ["low_usage_rate", "ยาที่มีอัตราการใช้น้อย"],
    ["drug_produced_in_hospital", "ยาผลิตในโรงพยาบาล"],
    ["original", "ยาต้นตำรับ"],
    ["no_need_prescription", "ไม่ต้องมีใบสั่งยา"],
    ["locked_if_age_is_outside_specified_range", "ล็อกเมื่ออายุนอกช่วงที่กำหนด"],
  ].map(([key, label]) => field(key, label, "checkbox")),
  field("highalert_description", "คำอธิบายความเสี่ยงสูง", "textarea"),
  enumeration("visibility", "ขอบเขตการสั่งยา"),
  ...[
    ["base_unit", "หน่วยย่อยที่สุด"],
    ["stock_unit", "หน่วยขาย"],
    ["container_unit", "หน่วยสั่งซื้อ"],
    ["default_dosage_unit", "หน่วยการใช้ยาเริ่มต้น"],
    ["unit_of_active_ingredient", "หน่วยตัวยาสำคัญ"],
    ["unit_of_per_quantity", "หน่วยปริมาณ"],
    ["unit_of_sale_quantity", "หน่วยจำนวนขาย"],
    ["unit_of_volumn_per_sale_unit", "หน่วยปริมาตรต่อหน่วยขาย"],
    ["dispense_unit", "หน่วยจ่าย"],
    ["default_volume_unit", "หน่วยปริมาตรผสม"],
    ["max_concentration_unit", "หน่วยความเข้มข้นสูงสุด"],
  ].map(([key, label]) => select(key, label, "unit")),
  ...[
    ["stock_size", "จำนวนหน่วยย่อยต่อหน่วยขาย"],
    ["active_ingredient_qty", "ปริมาณตัวยาสำคัญ"],
    ["default_unit_dose", "ขนาดยาเริ่มต้น"],
    ["default_volume_for_mixing", "ปริมาตรผสมเริ่มต้น"],
    ["default_iv_rate", "อัตราให้ยาเริ่มต้น"],
  ].map(([key, label]) => field(key, label, "decimal")),
  ...[
    ["container_size", "จำนวนต่อหน่วยสั่งซื้อ"],
    ["internal_pack_size", "ขนาดบรรจุภายใน"],
    ["external_pack_size", "ขนาดบรรจุภายนอก"],
    ["per_quantity_qty", "จำนวนหน่วยปริมาณ"],
    ["volumn_per_sale_unit", "ปริมาตรต่อหน่วยขาย"],
    ["default_duration", "ระยะเวลาเริ่มต้น"],
    ["minimum_age_year", "อายุต่ำสุด (ปี)"],
    ["minimum_age_month", "อายุต่ำสุด (เดือน)"],
    ["maximum_age_year", "อายุสูงสุด (ปี)"],
    ["maximum_age_month", "อายุสูงสุด (เดือน)"],
    ["limited_unit_dose", "ขนาดยาสูงสุด"],
    ["limited_duration", "ระยะเวลาสูงสุด"],
    ["limited_quantity", "จำนวนสูงสุด"],
    ["limited_reuse_duration", "ระยะเวลาก่อนใช้ซ้ำ"],
  ].map(([key, label]) => field(key, label, "number")),
  select("default_route", "วิธีให้ยาเริ่มต้น", "route"),
  select("default_site", "ตำแหน่งเริ่มต้น", "site"),
  select("default_frequency", "ความถี่เริ่มต้น", "frequency"),
  select("default_method", "วิธีใช้เริ่มต้น", "method"),
  select("default_prn", "เงื่อนไขใช้เมื่อจำเป็น", "prn"),
  select("limited_dosage_units", "หน่วยใช้ยาที่อนุญาต", "unit", true),
  select("limited_routes", "วิธีให้ยาที่อนุญาต", "route", true),
  select("limited_sites", "ตำแหน่งที่อนุญาต", "site", true),
  select("limited_frequencies", "ความถี่ที่อนุญาต", "frequency", true),
  select("limited_methods", "วิธีใช้ที่อนุญาต", "method", true),
  { ...field("default_solvent", "Default solvent", "remoteSelect"), remote: { kind: "DRUG" } },
  {
    ...field("limited_solvents", "ตัวทำละลายที่อนุญาต", "remoteSelect"),
    multiple: true,
    remote: { kind: "DRUG" },
  },
  select("limited_drug_drip_in", "หน่วยเวลาหยดยา", "unit", true),
  select("limited_iv_rate", "หน่วยอัตราการให้ยา", "unit", true),
  select("limited_drug_concentration", "หน่วยความเข้มข้นยา", "unit", true),
  { ...field("manufacturer", "ผู้ผลิต", "remoteSelect"), remote: { source: "manufacturer" } },
  field("remark", "หมายเหตุ", "textarea"),
  { ...field("product_image", "รูปยา", "image"), uiOnly: true },
  hidden("druggroup_set", "กลุ่มยา", "ความสัมพันธ์อยู่นอกขอบเขต"),
  hidden("drugrecommendation_set", "คำแนะนำยา", "ความสัมพันธ์อยู่นอกขอบเขต"),
  hidden("drugdosingrecommendation_set", "คำแนะนำขนาดยา", "ความสัมพันธ์อยู่นอกขอบเขต"),
  hidden("coverage_list", "สิทธิการรักษา", "ความสัมพันธ์อยู่นอกขอบเขต"),
  select("drug_type", "ประเภทยา", "catalogDrugType"),
  select("special_labels", "ฉลากพิเศษ", "catalogSpecialLabel", true),
  field("tmt_fsn_id", "Tmt fsn id", "text"),
  field("pack_size", "Pack size", "text"),
  field("default_solvent_quantity", "Default solvent quantity", "decimal"),
  field("max_concentration_rate", "Max concentration rate", "decimal"),
  field("max_rate_text", "Max rate text", "text"),
  field("add_auto_solvent", "เพิ่มนํ้าเกลืออัตโนมัติเมื่อเลือกยาตัวนี้", "checkbox"),
  field("total_dispense_unit", "จำนวนแบ่งทั้งหมด", "number"),
  field("percent_conversion", "Percent conversion", "decimal"),
  field("is_dispensed", "เป็นยาแบ่ง", "checkbox"),
  field("is_outside_drug", "ยาที่ไม่ใช้ในโรงพยาบาล", "checkbox"),
  field("is_syringe_need", "ต้องใช้เข็มฉีดยา", "checkbox"),
  field("is_taper_dose", "Taper dose", "checkbox"),
  field("phar_co_1", "Phar co 1", "text"),
  field("phar_co_2", "Phar co 2", "text"),
  field("code_x", "Code x", "text"),
  { ...field("factsheet_data", "Factsheet data", "textarea"), ignoreDefault: true },
  field("product_cat", "Product cat", "text"),
  enumeration("status", "Status"),
  field("spec_prep", "Spec prep", "text"),
  field("dfs_code", "Dfs code", "text"),
  field("content", "Content", "text"),
  field("distributor", "Distributor", "text"),
  field("ndc_24", "Ndc 24", "text"),
];

const drugSections: { section: string; fields: [string, string][] }[] = [
  {
    section: "ข้อมูลทั่วไปของยา",
    fields: [
      ["manufacturer", "Manufacturer"],
      ["visibility", "Visibility"],
      ["remark", "Remark"],
      ["drug_name", "Drug name"],
      ["pronunciation", "Pronunciation"],
      ["pronunciation_trade", "Pronunciation trade"],
      ["tmt_fsn_id", "Tmt fsn id"],
      ["ingredient_vtm", "Ingredient vtm display"],
      ["strength", "Strength"],
      ["container", "Container"],
      ["dosage_form", "Dosage form"],
      ["drug_name_format", "Drug name format"],
      ["custom_generic", "Custom generic"],
      ["generic_name", "ชื่อสามัญ"],
      ["synonym_name", "ชื่อสามัญอื่น"],
      ["drug_name_from_format", "ชื่อยาจากรูปแบบ"],
    ],
  },
  {
    section: "หน่วยและบรรจุภัณฑ์",
    fields: [
      ["base_unit", "Base unit"],
      ["stock_unit", "Stock unit"],
      ["stock_size", "Stock size"],
      ["container_unit", "Container unit"],
      ["container_size", "Container size"],
      ["internal_pack_size", "Internal pack size"],
      ["external_pack_size", "External pack size"],
      ["pack_size", "Pack size"],
    ],
  },
  {
    section: "ค่าเริ่มต้นการสั่งใช้ยา",
    fields: [
      ["default_unit_dose", "Default unit dose"],
      ["default_dosage_unit", "Default dosage unit"],
      ["default_route", "Default route"],
      ["default_site", "Default site"],
      ["default_frequency", "Default frequency"],
      ["default_prn", "Default prn"],
      ["default_method", "Default method"],
      ["default_duration", "Default duration"],
      ["default_solvent", "Default solvent"],
      ["default_solvent_quantity", "Default solvent quantity"],
      ["default_volume_for_mixing", "Default volume for mixing"],
      ["default_volume_unit", "Default volume unit"],
      ["default_iv_rate", "Default iv rate"],
    ],
  },
  {
    section: "ข้อจำกัดการสั่งใช้ยา",
    fields: [
      ["limited_unit_dose", "Limited unit dose"],
      ["limited_duration", "Limited duration"],
      ["limited_quantity", "Limited quantity"],
      ["limited_reuse_duration", "Limited reuse duration"],
      ["limited_dosage_units", "Limited dosage units"],
      ["limited_routes", "Limited routes"],
      ["limited_sites", "Limited sites"],
      ["limited_frequencies", "Limited frequencies"],
      ["limited_methods", "Limited methods"],
      ["max_concentration_rate", "Max concentration rate"],
      ["max_concentration_unit", "Max concentration unit"],
      ["max_rate_text", "Max rate text"],
      ["limited_solvents", "Limited solvents"],
      ["limited_drug_drip_in", "Limited drug drip in"],
      ["limited_iv_rate", "Limited iv rate"],
      ["limited_drug_concentration", "Limited drug concentration"],
      ["add_auto_solvent", "เพิ่มนํ้าเกลืออัตโนมัติเมื่อเลือกยาตัวนี้"],
    ],
  },
  {
    section: "การจัดประเภทยาและความปลอดภัย",
    fields: [
      ["classification", "Classification"],
      ["pregcat", "Pregcat"],
      ["pregcat_description", "Pregcat description"],
      ["total_dispense_unit", "จำนวนแบ่งทั้งหมด"],
      ["dispense_unit", "Dispense unit"],
      ["percent_conversion", "Percent conversion"],
      ["highalert_description", "Highalert description"],
      ["is_dispensed", "เป็นยาแบ่ง"],
      ["highalert", "Highalert"],
      ["lasa", "Lasa"],
      ["original", "Original"],
      ["stat", "Stat"],
      ["vital", "Vital"],
      ["no_need_prescription", "ยาที่สามารถเรียกซื้อได้โดยไม่ต้องใช้ใบสั่งยา"],
      ["individual_use", "Individual use"],
      ["to_be_tracked", "To be tracked"],
      ["research_drug", "Research drug"],
      ["low_usage_rate", "Low usage rate"],
      ["drug_produced_in_hospital", "Drug produced in hospital"],
      ["is_outside_drug", "ยาที่ไม่ใช้ในโรงพยาบาล"],
      ["is_syringe_need", "ต้องใช้เข็มฉีดยา"],
      ["is_taper_dose", "Taper dose"],
    ],
  },
  {
    section: "รหัส ข้อบ่งใช้ และฉลากยา",
    fields: [
      ["phar_co_1", "Phar co 1"],
      ["phar_co_2", "Phar co 2"],
      ["code_x", "Code x"],
      ["factsheet", "Factsheet"],
      ["factsheet_data", "Factsheet data"],
      ["indication_th", "Indication th"],
      ["indication_en", "Indication en"],
      ["helper_labels", "Helper labels"],
      ["special_labels", "Special labels"],
      ["extra_note", "Extra note"],
      ["drug_list", "Drug list"],
      ["drug_list_legacy", "Drug list legacy"],
      ["drug_type", "Drug type"],
      ["product_cat", "Product cat"],
      ["status", "Status"],
    ],
  },
  {
    section: "ปริมาณ อายุผู้ใช้ และข้อมูลผู้จัดจำหน่าย",
    fields: [
      ["active_ingredient_qty", "ปริมาณตัวยาสำคัญ"],
      ["unit_of_active_ingredient", "หน่วยของปริมาณตัวยาสำคัญ"],
      ["per_quantity_qty", "จำนวนหน่วยปริมาณ"],
      ["unit_of_per_quantity", "หน่วยปริมาณ"],
      ["unit_of_volumn_per_sale_unit", "หน่วยของปริมาณต่อหน่วยขาย"],
      ["minimum_age_year", "อายุต่ำสุดที่อนุญาตให้ใช้ยา (ปี)"],
      ["minimum_age_month", "อายุต่ำสุดที่อนุญาตให้ใช้ยา (เดือน)"],
      ["maximum_age_year", "อายุสูงสุดที่อนุญาตให้ใช้ยา (ปี)"],
      ["maximum_age_month", "อายุสูงสุดที่อนุญาตให้ใช้ยา (เดือน)"],
      ["spec_prep", "Spec prep"],
      ["dfs_code", "Dfs code"],
      ["content", "Content"],
      ["distributor", "Distributor"],
      ["ndc_24", "Ndc 24"],
      ["locked_if_age_is_outside_specified_range", "ไม่อนุญาตให้สั่งหากอายุเกินช่วงที่กำหนด"],
      ["unit_of_sale_quantity", "หน่วยจำนวนขาย"],
      ["volumn_per_sale_unit", "ปริมาตรต่อหน่วยขาย"],
    ],
  },
  {
    section: "รายการที่เกี่ยวข้อง",
    fields: [
      ["coverage_list", "Coverage list"],
      ["drug_ingredients", "Drug ingredients"],
      ["druggroup_set", "Druggroup-drug relationships"],
      ["product_image", "Drug images"],
      ["drugrecommendation_set", "Drugrecommendation-drug relationships"],
      ["drugdosingrecommendation_set", "Drugdosingrecommendation-drug relationships"],
    ],
  },
];
const groupedDrugFields = drugSections.flatMap(({ section, fields }) =>
  fields.map(([key, label]) => {
    const definition = drugFields.find((field) => field.key === key);
    if (!definition) throw new Error(`Unknown drug field: ${key}`);
    return { ...definition, label, section };
  })
);

const kind = (key: string, label: string, fields: FieldSchema[] = []): KindSchema => ({
  kind: key,
  label,
  enabled: true,
  fields: [...commonFields, ...fields],
});

export const serviceKindSchema: KindSchema[] = [
  kind("MISCELLANEOUS", "บริการทั่วไป"),
  kind("DOCTOR_FEE", "ค่าธรรมเนียมแพทย์", [
    select("category", "หมวดค่าธรรมเนียม", "dfRuleCategory"),
    hidden("only_divisions", "แสดงเฉพาะหน่วยงาน", "รอยืนยันขอบเขต division"),
    field("active", "ใช้งาน", "checkbox"),
    field("display_seq", "ลำดับแสดงผล", "number"),
  ]),
  kind("SUPPLY", "เวชภัณฑ์", [
    enumeration("mode", "Mode"),
    select("base_unit", "Base unit", "unitCore"),
    select("stock_unit", "Stock unit", "unitCore"),
    field("stock_size", "Stock size", "decimal"),
    {
      ...field("manufacturer", "Manufacturer", "remoteSelect"),
      remote: { source: "manufacturer" },
    },
  ]),
  kind("TREATMENT", "หัตถการ", [
    enumeration("type", "Type"),
    field("suggestion", "คำแนะนำในการปฏิบัติตัว", "textarea"),
    field("suggestion_en", "คำแนะนำในการปฏิบัติตัวภาษาอังกฤษ", "textarea"),
    field("restrict_flag", "จำกัดช่วงเวลา", "checkbox"),
  ]),
  kind("IMAGING", "รังสีวินิจฉัย", [
    field("acronym", "ชื่อย่อ"),
    field("xray_code", "Xray code"),
    { ...field("modality", "Modality"), placeholder: "เช่น CR, US, MR, CT" },
    select("xray_group", "Xray group", "imagingTestGroup"),
  ]),
  kind("ROOM_RATE", "ค่าห้อง", [
    select("room_type", "Room type", "roomType", true),
    field("max_hours", "ชั่วโมงสูงสุดสำหรับอัตราราคาห้องพัก", "number"),
    field("is_main", "Is main", "checkbox"),
    field("active", "Active", "checkbox"),
  ]),
  kind("NURSE_RATE", "ค่าบริการพยาบาล", [
    select("room_type", "Room type", "roomType", true),
    field("active", "Active", "checkbox"),
  ]),
  kind("LAB", "แล็บ", [
    field("acronym", "ชื่อย่อ"),
    field("lab_code", "Lab code"),
    select("lab_division", "หน่วยงาน Lab", "labDivision"),
    select("specimen", "Specimen", "specimenList"),
    field("is_default", "เป็น Specimen default", "checkbox"),
    field("restrict_flag", "ต้องทำการในช่วงเวลาที่กำหนด", "checkbox"),
    enumeration("lab_speciality", "ชนิดเฉพาะของ Lab"),
    enumeration("lab_type", "ชนิดการส่งตรวจ"),
    select("lab_unit", "Lab unit", "catalogLabUnit"),
    field("lab_tmlt", "Lab tmlt"),
    field("is_confidential", "ปกปิดผล Lab", "checkbox"),
    field("name_confidential", "ปกปิดชื่อ Lab", "checkbox"),
    select("organs", "อวัยวะ", "catalogLabOrgan", true),
    ...[
      field("ref_value_min", "Reference value min", "decimal"),
      field("ref_value_max", "Reference value max", "decimal"),
      field("ref_value_txt", "Reference value in text", "textarea"),
      field("critical_value_min", "ค่าวิกฤต min", "decimal"),
      field("critical_value_max", "ค่าวิกฤต max", "decimal"),
    ].map((item) => ({ ...item, subsection: "ค่าอ้างอิง" })),
    ...[
      field("turn_around_time_hr", "ชั่วโมง (hours)", "number"),
      field("turn_around_time_min", "นาที (mins)", "number"),
    ].map((item) => ({ ...item, subsection: "ระยะเวลารอผล (Turn around time)" })),
    ...[
      field("recommendation_low", "Recommendation low", "textarea"),
      field("recommendation_normal", "Recommendation normal", "textarea"),
      field("recommendation_high", "Recommendation high", "textarea"),
    ].map((item) => ({ ...item, subsection: "คำแนะนำตามผลตรวจ" })),
    {
      ...field("alternative_names", "Alternative Lab Test Name", "textList"),
      subsection: "Alternative lab test names",
    },
    {
      ...field("is_child_lab", "Lab นี้เป็น Lab ลูก (ผูกกับ Lab แม่)", "checkbox"),
      uiOnly: true,
      dependsOn: "parent_lab",
      subsection: "Lab ลูก",
      subsectionCard: true,
    },
    {
      ...field("parent_lab", "Lab แม่", "remoteSelect"),
      showWhen: "is_child_lab",
      remote: { kind: "LAB", editableOnly: true, excludeSelf: true },
      subsection: "Lab ลูก",
      subsectionCard: true,
    },
  ]),
  kind("DRUG", "ยา", groupedDrugFields),
  kind("FOOD_TYPE", "อาหาร", [
    { ...enumeration("food_type", "ประเภทอาหาร"), defaultValue: "G" },
    { ...enumeration("calculate_payment", "การคิดค่าบริการ"), defaultValue: "I" },
  ]),
  kind("GENERIC", "บริการเดิม"),
];

export const productTypeCodes: Record<string, readonly string[]> = {
  FOOD_TYPE: ["DIET"],
  LAB: ["LAB", "BLOOD"],
  IMAGING: ["XRAY"],
  TREATMENT: ["PROC", "REHAB"],
  SUPPLY: ["SUPPLY", "EQUIP"],
  DRUG: ["DRUG"],
  DOCTOR_FEE: ["DF"],
  ROOM_RATE: ["ROOM"],
};
const miscellaneousExcludedCodes = new Set([
  ...Object.values(productTypeCodes).flat(),
  "DIET",
  "SERVICE_USAGE",
  "PACKAGE",
]);
export const filterProductTypes = (kind: string | undefined, options: SelectOption[]) => {
  if (kind === "MISCELLANEOUS")
    return options.filter((option) => !miscellaneousExcludedCodes.has(option.code || ""));
  const codes = kind ? productTypeCodes[kind] : undefined;
  return codes ? options.filter((option) => codes.includes(option.code || "")) : options;
};
export const serviceCatalogFeatures = { description: false };
export const isFieldEnabled = (field: FieldSchema) =>
  !field.hidden && (!field.feature || serviceCatalogFeatures[field.feature]);
export const normalizeTextList = (value: unknown): string[] =>
  Array.isArray(value)
    ? Array.from(
        new Set(
          value
            .filter((item): item is string => typeof item === "string")
            .map((item) => item.trim())
            .filter(Boolean)
        )
      )
    : [];
export const serviceMetadata = (
  metadata: Metadata,
  values: Record<string, any> = {},
  creating = false
): Metadata => ({
  ...metadata,
  p_type: { ...metadata.p_type, required: true },
  ...(creating && metadata.unit && { unit: { ...metadata.unit, required: true } }),
  ...(metadata.parent_lab && {
    parent_lab: { ...metadata.parent_lab, required: !!values.is_child_lab },
  }),
});

export const getKindSchema = (kindKey: string) =>
  serviceKindSchema.find((item) => item.kind === kindKey);
export const getFields = (kindKey: string, metadata: Metadata) =>
  (getKindSchema(kindKey)?.fields || []).filter(
    (item) =>
      isFieldEnabled(item) &&
      (item.uiOnly ? !item.dependsOn || metadata[item.dependsOn] : metadata[item.key])
  );
export const childMetadata = (metadata?: FieldMetadata): Metadata =>
  metadata?.child?.children || metadata?.children || {};
export const fieldOptions = (
  schema: FieldSchema,
  metadata: FieldMetadata = {},
  masters: Record<string, SelectOption[]> = {},
  kind?: string
): SelectOption[] => {
  if (schema.options && "enum" in schema.options) {
    return (metadata.choices || []).map(({ value, display_name }) => ({
      key: value,
      value,
      text: display_name,
    }));
  }
  const options =
    schema.options && "master" in schema.options ? masters[schema.options.master] || [] : [];
  return schema.key === "p_type" ? filterProductTypes(kind, options) : options;
};
